import React, { useCallback, useEffect, useRef, useState } from "react";
import type * as LType from "leaflet";
import "proj4";
import type { ActivityAddress } from "../types";

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
  };
}

interface Props {
  addresses: ActivityAddress[];
  onAddressesChange: (addresses: ActivityAddress[]) => void;
  svgSearch: string;
  searchButtonText?: string;
  descriptionOfAddressField?: string;
}

const MAP_PROXY_SEARCH_URL = "/api/map-proxy/search";
const DEFAULT_CENTER: [number, number] = [57.7, 12.0];
const DEFAULT_ZOOM = 8;

const formatAddress = (result: NominatimResult) => {
  const address = result.address;
  if (!address) return result.display_name;

  const street = [address.road, address.house_number].filter(Boolean).join(" ");
  const locality = address.city ?? address.town ?? address.village ?? address.municipality;
  const postalAddress = [address.postcode, locality].filter(Boolean).join(" ");
  const formatted = [street, postalAddress].filter(Boolean).join(", ");

  return formatted || result.display_name;
};

export const AddressSearchMap: React.FC<Props> = ({
  addresses,
  onAddressesChange,
  svgSearch,
  searchButtonText = "Sök",
  descriptionOfAddressField
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LType.Map | null>(null);
  const markerRefs = useRef<LType.Marker[]>([]);
  const addressesRef = useRef(addresses);
  const onAddressesChangeRef = useRef(onAddressesChange);
  const leafletRef = useRef<typeof LType | null>(null);
  const listboxId = "address-search-listbox";
  const suggestionStatus = isSearching
    ? "Söker adressförslag"
    : showSuggestions
      ? suggestions.length === 1
        ? "1 adressförslag tillgängligt. Använd upp- och nedpilarna för att välja."
        : suggestions.length > 1
          ? `${suggestions.length} adressförslag tillgängliga. Använd upp- och nedpilarna för att välja.`
          : "Inga adressförslag hittades."
      : "";

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    (async () => {
      const leafletModule = await import("leaflet");
      await import("proj4leaflet");
      if (cancelled || !containerRef.current) return;

      const L = ((leafletModule as unknown as { default?: typeof LType }).default ?? leafletModule) as typeof LType;
      leafletRef.current = L;

      const projectedL = L as typeof LType & {
        Proj: {
          CRS: new (
            code: string,
            definition: string,
            options: {
              resolutions: number[];
              origin: [number, number];
              bounds: LType.Bounds;
            },
          ) => LType.CRS;
        };
      };
      const crs = new projectedL.Proj.CRS(
        "EPSG:3006",
        "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
        {
          resolutions: [4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5],
          origin: [-1200000, 8500000],
          bounds: L.bounds([-1200000, 8500000], [4305696, 2994304]),
        },
      );

      const initialAddress = addressesRef.current[0];
      const initialCenter: [number, number] = initialAddress
        ? [initialAddress.latitude, initialAddress.longitude]
        : DEFAULT_CENTER;
      const initialZoom = initialAddress ? 13 : DEFAULT_ZOOM;

      const map = L.map(containerRef.current, {
        crs,
        minZoom: 0,
        maxZoom: 13,
      }).setView(initialCenter, initialZoom);
      L.tileLayer("/api/map-proxy/{z}/{x}/{y}", {
        attribution: "© Lantmäteriet",
        minZoom: 0,
        maxZoom: 13,
      }).addTo(map);
      mapRef.current = map;

      syncMarkers(L, map, addressesRef.current);
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      markerRefs.current = [];
      leafletRef.current = null;
    };
  }, []);

  const syncMarkers = useCallback((L: typeof LType, map: LType.Map, nextAddresses: ActivityAddress[]) => {
    markerRefs.current.forEach((marker) => marker.remove());
    markerRefs.current = nextAddresses.map((location, index) => {
      const marker = L.marker([location.latitude, location.longitude], {
        draggable: true,
        title: "Dra markören för att justera platsen",
        icon: L.icon({
          iconUrl: "/icons/visuell-identitet/leaflet/marker-icon.png",
          iconRetinaUrl: "/icons/visuell-identitet/leaflet/marker-icon-2x.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          crossOrigin: false,
        }),
      });
      marker.on("dragend", () => {
        const { lat: newLat, lng: newLng } = marker.getLatLng();
        const next = [...addressesRef.current];
        next[index] = { ...next[index], latitude: newLat, longitude: newLng };
        onAddressesChangeRef.current(next);
      });
      marker.addTo(map);
      return marker;
    });
  }, []);

  useEffect(() => {
    addressesRef.current = addresses;
    onAddressesChangeRef.current = onAddressesChange;
    if (mapRef.current && leafletRef.current) {
      syncMarkers(leafletRef.current, mapRef.current, addresses);
    }
  }, [addresses, onAddressesChange, syncMarkers]);

  const geocode = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setIsSearching(true);
    try {
      const url = `${MAP_PROXY_SEARCH_URL}?q=${encodeURIComponent(q.trim())}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: NominatimResult[] = await res.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && activeIndex < 0 && inputValue.trim().length >= 3) {
      e.preventDefault();
      void geocode(inputValue);
      return;
    }
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  const handleSuggestionSelect = (result: NominatimResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
    const nextAddresses = [...addressesRef.current, {
      address: formatAddress(result),
      latitude: lat,
      longitude: lng,
    }];
    onAddressesChangeRef.current(nextAddresses);

    if (mapRef.current && leafletRef.current) {
      mapRef.current.setView([lat, lng], 14);
      syncMarkers(leafletRef.current, mapRef.current, nextAddresses);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      setActiveIndex(-1);
      setSuggestions([]);
      setShowSuggestions(false);
  };

  const onSearchAdressClick = () => {
    void geocode(inputValue);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const searchRow = e.currentTarget.parentElement;
    window.setTimeout(() => {
      if (searchRow?.contains(document.activeElement)) return;
      setShowSuggestions(false);
      setActiveIndex(-1);
    }, 200);
  };

  return (
    <div className="address-search-map">
      <div className="address-search-map__input-wrapper">
        <label htmlFor="address-search">
          Adress
          <span className="ms-1 invalid" aria-hidden="true">
            *
          </span>
        </label>
        {descriptionOfAddressField && <p>{descriptionOfAddressField}</p>}
        <div className="address-search-map__search-row">
          <input
            id="address-search"
            className="address-search-map__input"
            type="search"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder="Sök adress..."
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? `address-suggestion-${activeIndex}` : undefined}
            aria-label="Adress för aktiviteten"
          />
          <button type="button" name="button" className="button-primary" onClick={onSearchAdressClick}>
              {svgSearch && (
                <svg aria-hidden="true" focusable="false" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <use href={svgSearch} />
                </svg>
              )}
            <span>{searchButtonText}</span>
          </button>
        </div>
        <span role="status" aria-atomic="true" className="sr-only">{suggestionStatus}</span>
        {showSuggestions && suggestions.length > 0 && (
          <div className="address-search-map__suggestions">
            <ul
              id={listboxId}
              role="listbox"
              className="autocomplete-resultheader autocomplete__list"
              aria-label="Adressförslag"
            >
              {suggestions.map((s, i) => (
                <li
                  key={s.place_id}
                  id={`address-suggestion-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  className={`autocomplete__item autocomplete__item--search${i === activeIndex ? " autocomplete__item--active" : ""}`}
                  onMouseDown={() => handleSuggestionSelect(s)}
                >
                  <span className="autocomplete__link address-search-map__link">{formatAddress(s)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        className="address-search-map__map"
      />
      {addresses.length > 0 && (
        <ul className="address-search-map__selected" aria-label="Valda adresser">
          {addresses.map((location, index) => (
            <li key={`${location.address}-${index}`}>
              <span>{location.address}</span>
              <button
                type="button"
                onClick={() => onAddressesChange(addresses.filter((_, i) => i !== index))}
                aria-label={`Ta bort ${location.address}`}
              >
                Ta bort
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
